import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../components/ui/theme-provider'
import { useCategories } from '../hooks/useCategories'
import { supabase } from '../lib/supabase'
import { Car } from '../types/database'

import { Search, MapPin, Calendar, Clock, Star, Award, ArrowRight, Play, ChevronLeft, ChevronRight, Car as CarIcon, User, Building2, Users, Shield, Zap, CheckCircle } from 'lucide-react'
import { Icon } from '@iconify/react'
import { generateCarSlug, generateOrganizationSchema, generateWebSiteSchema, generateBreadcrumbSchema, generateFAQSchema } from '../lib/seo'
import { getCanonicalUrl } from '../lib/canonicalUrl'
import { motion, useReducedMotion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { DatePicker } from '../components/ui/date-picker'
import LocationSuggestionSelect from '../components/ui/location-suggestion-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import BrandsSlider from '../components/BrandsSlider'
import { SearchPanel } from '../components/SearchPanel'
import PopularDestinations from '../components/PopularDestinations'
import FAQ from '../components/FAQ'
import { generateCategoryUrl } from '../utils/categorySeoUrls'
import { mockLocations } from '../data/locations'
import { useIsMobile } from '@/hooks/use-mobile'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet'
import SEOHead from '../components/seo/SEOHead'
import { useWatermarkedImage } from '../hooks/useWatermarkedImage'

const Home: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()
  const { theme } = useTheme()
  const { categories, loading: categoriesLoading, error: categoriesError } = useCategories()
  const shouldReduceMotion = useReducedMotion()
  const isMobile = useIsMobile()
  
  // Debug categories loading
  React.useEffect(() => {
    // Categories debug info ready if needed
  }, [categories, categoriesLoading, categoriesError])
  
  // Professional animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: shouldReduceMotion ? 0 : 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: shouldReduceMotion ? 0 : 0.6, ease: "easeOut" as const }
  }
  
  const fadeInLeft = {
    initial: { opacity: 0, x: shouldReduceMotion ? 0 : -30 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: shouldReduceMotion ? 0 : 0.7, ease: "easeOut" as const }
  }
  
  const fadeInRight = {
    initial: { opacity: 0, x: shouldReduceMotion ? 0 : 30 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: shouldReduceMotion ? 0 : 0.7, ease: "easeOut" as const }
  }
  
  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  }
  
  // Validate language parameter - only allow valid language codes
  const validLanguages = ['en', 'fr', 'ar']
  const currentLang = lang && validLanguages.includes(lang) ? lang : 'fr'
  
  // Handle email verification if user lands on homepage after clicking email link
  useEffect(() => {
    const handleEmailVerification = async () => {
      const urlParams = new URLSearchParams(window.location.search)
      const token = urlParams.get('token')
      const type = urlParams.get('type')
      
      // Check if this is an email verification callback
      if (token && (type === 'signup' || type === 'email_confirmation')) {
        console.log('Home: Email verification detected, redirecting to email-confirmed page')
        
        // Get the current language from the URL or localStorage
        const currentLang = lang || localStorage.getItem('kiracar-language') || 'fr'
        
        // Redirect to the email confirmed page
        navigate(`/${currentLang}/email-confirmed`, { replace: true })
        return
      }
    }
    
    handleEmailVerification()
  }, [navigate, lang])
  
  // Search form state
  const [searchLocation, setSearchLocation] = useState<{
    name: string;
    address: string;
    city: string;
    coordinates?: { lat: number; lng: number };
  } | null>(null)
  const [pickupDate, setPickupDate] = useState(new Date())
  const [returnDate, setReturnDate] = useState(new Date(Date.now() + 24 * 60 * 60 * 1000))
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [isTyping, setIsTyping] = useState(false)

  // Recent cars state
  const [recentCars, setRecentCars] = useState<Car[]>([])
  const [recentCarsLoading, setRecentCarsLoading] = useState(true)

  // Categories scroll state
  const [categoriesScrollRef, setCategoriesScrollRef] = useState<HTMLDivElement | null>(null)
  const [showScrollArrows, setShowScrollArrows] = useState(false)

  // Categories scroll functions
  const scrollCategoriesLeft = () => {
    if (categoriesScrollRef) {
      categoriesScrollRef.scrollBy({
        left: -300,
        behavior: 'smooth'
      })
    }
  }

  const scrollCategoriesRight = () => {
    if (categoriesScrollRef) {
      categoriesScrollRef.scrollBy({
        left: 300,
        behavior: 'smooth'
      })
    }
  }

  // Center categories on load
  useEffect(() => {
    if (categoriesScrollRef && categories.length > 0 && !categoriesLoading) {
      // Use setTimeout to ensure DOM is fully rendered
      const timeoutId = setTimeout(() => {
        const scrollContainer = categoriesScrollRef
        const containerWidth = scrollContainer.offsetWidth
        const scrollWidth = scrollContainer.scrollWidth
        
        if (scrollWidth > containerWidth) {
          const centerPosition = (scrollWidth - containerWidth) / 2
          
          scrollContainer.scrollTo({
            left: centerPosition,
            behavior: 'smooth'
          })
        }
      }, 100) // Small delay to ensure content is rendered
      
      return () => clearTimeout(timeoutId)
    }
  }, [categoriesScrollRef, categories, categoriesLoading])

  // Map database category names to URL slugs
  const mapCategoryNameToSlug = (categoryName: string): string => {
    // Special mappings for categories that need specific URL slugs
    const specialMappings: Record<string, string> = {
      'Sedan': 'berline',  // Sedan -> berline for French URL
      'Luxury': 'luxury',  // Keep luxury as is
      'SUV': 'suv',        // Keep SUV as is
      'Compact': 'compact', // Keep compact as is
      'Electric': 'electric', // Keep electric as is
      'Large': 'large',    // Keep large as is
      'Medium': 'medium',  // Keep medium as is
      'Pickup': 'pickup',  // Keep pickup as is
      'Sport': 'sport',    // Keep sport as is
      'Cabriolet': 'cabriolet', // Keep cabriolet as is
    }
    
    // Check special mappings first
    if (specialMappings[categoryName]) {
      return specialMappings[categoryName];
    }
    
    // For new categories, use a smart default:
    // 1. Try exact match first
    // 2. Try lowercase version
    // 3. Try to create a URL-friendly slug
    const slug = categoryName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')  // Replace non-alphanumeric with hyphens
      .replace(/-+/g, '-')          // Replace multiple hyphens with single
      .replace(/^-|-$/g, '');       // Remove leading/trailing hyphens
    
    return slug;
  }

  // Check if scrolling is needed
  const checkScrollable = () => {
    if (categoriesScrollRef && categories.filter(category => category.car_count > 0).length > 0) {
      const { scrollWidth, clientWidth } = categoriesScrollRef
      setShowScrollArrows(scrollWidth > clientWidth)
    }
  }

  // Check scrollable when categories load or ref changes
  useEffect(() => {
    checkScrollable()
    
    if (categoriesScrollRef) {
      const handleResize = () => {
        checkScrollable()
        // Re-center on resize
        if (categories.length > 0 && !categoriesLoading) {
          setTimeout(() => {
            const scrollContainer = categoriesScrollRef
            const containerWidth = scrollContainer.offsetWidth
            const scrollWidth = scrollContainer.scrollWidth
            
            if (scrollWidth > containerWidth) {
              const centerPosition = (scrollWidth - containerWidth) / 2
              scrollContainer.scrollTo({
                left: centerPosition,
                behavior: 'smooth'
              })
            }
          }, 100)
        }
      }
      
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [categoriesScrollRef, categories, categoriesLoading])
  
  // Fetch recent cars
  const fetchRecentCars = async () => {
    try {
      setRecentCarsLoading(true)
      const { data, error } = await supabase
        .from('cars')
        .select(`
          *,
          agencies (
            id,
            name,
            city
          )
        `)
        .eq('availability', true)
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) {
        console.error('Error fetching recent cars:', error)
        return
      }

      setRecentCars(data || [])
    } catch (error) {
      console.error('Error fetching recent cars:', error)
    } finally {
      setRecentCarsLoading(false)
    }
  }

  useEffect(() => {
    fetchRecentCars()
  }, [])

  // Car Card Component with Image Carousel
  const CarCard: React.FC<{ car: Car }> = ({ car }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    // Use original images array (for indexing)
    const originalImages = car.images && car.images.length > 0 ? car.images : ['/images/cars/default-car.jpg']
    // Get watermarked images - use pre-watermarked if available, otherwise use hook fallback
    const watermarkedImages = car.watermarked_images || []
    const currentOriginalImage = originalImages[currentImageIndex]
    const currentWatermarkedImage = watermarkedImages[currentImageIndex]
    // Use hook for fallback when pre-watermarked not available
    const hookWatermarkedImage = useWatermarkedImage(currentOriginalImage)
    const currentImage = currentWatermarkedImage || hookWatermarkedImage
    
    const nextImage = (e: React.MouseEvent) => {
      e.stopPropagation()
      setCurrentImageIndex((prev) => (prev + 1) % originalImages.length)
    }
    
    const prevImage = (e: React.MouseEvent) => {
      e.stopPropagation()
      setCurrentImageIndex((prev) => (prev - 1 + originalImages.length) % originalImages.length)
    }

    const agency = (car as any).agencies

    return (
      <motion.div
        initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 15 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ 
          duration: shouldReduceMotion ? 0 : 0.4, 
          ease: [0.25, 0.46, 0.45, 0.94]
        }}
        viewport={{ once: true }}
        className="group cursor-pointer"
        onClick={() => navigate(`/${lang || 'fr'}/cars/${generateCarSlug(car, undefined, (lang as 'fr' | 'en' | 'ar') || 'fr')}`)}
      >
        <Card className="border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden bg-white dark:bg-card">
          <div className="relative">
            {/* Image Carousel */}
            <div className="relative h-48 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700">
              {currentOriginalImage && currentOriginalImage !== '/images/cars/default-car.jpg' ? (
              <img
                src={currentImage}
                alt={`${car.make} ${car.model}`}
                width="400"
                height="192"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `
                        <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700">
                          <div class="text-center text-gray-400 dark:text-gray-500">
                            <svg class="w-16 h-16 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                            </svg>
                            <p class="text-sm font-medium">${car.make} ${car.model}</p>
                            <p class="text-xs">${car.year}</p>
                          </div>
                        </div>
                      `;
                    }
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700">
                  <div className="text-center text-gray-400 dark:text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm font-medium">{car.make} {car.model}</p>
                    <p className="text-xs">{car.year}</p>
                  </div>
                </div>
              )}
              
              {/* Image Navigation */}
              {originalImages.length > 1 && currentOriginalImage && currentOriginalImage !== '/images/cars/default-car.jpg' && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  
                  {/* Image Indicators */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
                    {originalImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation()
                          setCurrentImageIndex(index)
                        }}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          index === currentImageIndex 
                            ? 'bg-white' 
                            : 'bg-white/50 hover:bg-white/75'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* New Badge */}
            <Badge className="absolute top-3 left-3 bg-brand-500 text-black dark:text-black font-semibold">
              Nouveau
            </Badge>
            
            {/* Rating */}
            <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm rounded-full px-2 py-1 flex items-center space-x-1">
              <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
              <span className="text-white text-xs font-semibold">4.8</span>
            </div>
          </div>
          
          <CardContent className="p-3 md:p-4">
            <h3 className="font-bold text-foreground mb-2 text-base md:text-lg">
              {car.make} {car.model} {car.year}
            </h3>
            <div className="flex items-center text-xs md:text-sm text-muted-foreground mb-2 md:mb-3">
              <MapPin className="h-3 w-3 md:h-4 md:w-4 mr-1" />
              {agency?.city || 'Morocco'}
            </div>
            <div className="flex flex-wrap gap-1 mb-2 md:mb-3">
              {[
                car.transmission === 'automatic' ? 'Automatique' : 'Manuelle',
                `${car.seats} Places`,
                car.features?.[0] || 'Climatisation'
              ].slice(0, 2).map((feature, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {feature}
                </Badge>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-lg md:text-2xl font-bold text-foreground">{car.price_per_day}</span>
                <span className="text-xs md:text-sm font-medium text-muted-foreground ml-1">{t('cars.currency')}</span>
                <span className="text-muted-foreground text-xs md:text-sm">{t('cars.perDay')}</span>
              </div>
              <Button size="sm" className="bg-brand-500 hover:bg-brand-600 text-black dark:text-black font-semibold text-xs md:text-sm px-3 md:px-4">
                {t('rent')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  const handleSearch = () => {
    const searchParams = new URLSearchParams({
      location: searchLocation ? `${searchLocation.name}, ${searchLocation.city}` : '',
      pickupDate: pickupDate.toISOString(),
      returnDate: returnDate.toISOString(),
    })
    
    // Add category if selected (but not "all")
    if (selectedCategory && selectedCategory !== 'all') {
      searchParams.set('category', selectedCategory)
    }
    
    navigate(`/${lang || 'fr'}/cars?${searchParams.toString()}`)
  }

  // Featured cars data with real car images
  const featuredCars = [
    {
      id: 1,
      name: 'BMW 3 Series GT',
      year: 2023,
      image: '/images/cars/3_series_gt.jpg',
      rating: 4.8,
      reviews: 124,
      price: 89,
      features: ['Manual', '5 Passengers', 'Air Conditioning'],
      category: 'Luxury'
    },
    {
      id: 2,
      name: 'Mercedes C-Class',
      year: 2024,
      image: '/images/cars/c_class.jpg',
      rating: 4.9,
      reviews: 98,
      price: 95,
      features: ['Automatic', '5 Passengers', 'Premium'],
      category: 'Premium'
    },
    {
      id: 3,
      name: 'Audi A4',
      year: 2023,
      image: '/images/cars/a4.jpg',
      rating: 4.7,
      reviews: 156,
      price: 85,
      features: ['Manual', '5 Passengers', 'Sport'],
      category: 'Executive'
    },
    {
      id: 4,
      name: 'BMW 4 Series Convertible',
      year: 2024,
      image: '/images/cars/4_series_convertible.jpg',
      rating: 4.9,
      reviews: 87,
      price: 120,
      features: ['Convertible', '4 Passengers', 'Sport'],
      category: 'Convertible'
    }
  ]

  // How it works steps
  const steps = [
    {
      icon: MapPin,
      title: t('chooseLocation'),
      description: t('chooseLocationDesc'),
      color: 'bg-brand-500'
    },
    {
      icon: Calendar,
      title: t('pickupDate'),
      description: t('pickupDateDesc'),
      color: 'bg-brand-500'
    },
    {
      icon: Award,
      title: t('bookYourCar'),
      description: t('bookYourCarDesc'),
      color: 'bg-brand-400'
    }
  ]

  // SEO Metadata
  const pageTitle = lang === 'ar' ?
    'كيراكار - أفضل منصة لتأجير السيارات في المغرب | أسعار تنافسية وحجز آمن' :
    lang === 'en' ?
      'KiraCar - Best Car Rental Platform in Morocco | Competitive Prices & Secure Booking' :
      'KiraCar - Meilleure Plateforme de Location de Voitures au Maroc | Prix Compétitifs & Réservation Sécurisée'

  const pageDescription = lang === 'ar' ?
    'استأجر سيارتك المثالية في المغرب مع كيراكار. اكتشف آلاف السيارات من وكالات موثوقة، قارن الأسعار، واحجز بسهولة عبر الإنترنت. أسعار تنافسية، خدمة 24/7، وحجز آمن مضمون.' :
    lang === 'en' ?
      'Rent your perfect car in Morocco with KiraCar. Discover thousands of vehicles from trusted agencies, compare prices, and book easily online. Competitive rates, 24/7 support, and secure booking guaranteed.' :
      'Louez votre voiture idéale au Maroc avec KiraCar. Découvrez des milliers de véhicules auprès d\'agences de confiance, comparez les prix et réservez facilement en ligne. Tarifs compétitifs, assistance 24/7 et réservation sécurisée garantie.'

  const pageKeywords = lang === 'ar' ?
    'تأجير سيارات المغرب, كيراكار, إيجار السيارات الدار البيضاء, تأجير سيارات الرباط, تأجير سيارات مراكش, location voiture maroc, car rental morocco' :
    lang === 'en' ?
      'car rental morocco, rent car morocco, car hire morocco, KiraCar, casablanca car rental, rabat car rental, marrakech car rental, agadir car rental, cheap car rental morocco' :
      'location voiture maroc, location auto maroc, KiraCar, location voiture casablanca, location voiture rabat, location voiture marrakech, location voiture agadir, rent car morocco'

  const currentUrl = getCanonicalUrl(`${lang || 'fr'}`)

  // FAQ Data
  const faqs = lang === 'ar' ? [
    {
      question: 'كيف يمكنني حجز سيارة على كيراكار؟',
      answer: 'يمكنك بسهولة حجز سيارة على كيراكار من خلال البحث عن السيارة المفضلة لديك، واختيار تواريخ الاستئجار، ثم إكمال عملية الحجز عبر الإنترنت. نقدم عملية حجز آمنة وسريعة مع تأكيد فوري.'
    },
    {
      question: 'ما هي المستندات المطلوبة لاستئجار سيارة؟',
      answer: 'تحتاج إلى رخصة قيادة سارية المفعول، وبطاقة هوية أو جواز سفر، وبطاقة ائتمان للإيداع. بعض الوكالات قد تطلب مستندات إضافية حسب نوع السيارة.'
    },
    {
      question: 'هل يمكنني إلغاء أو تعديل حجزي؟',
      answer: 'نعم، يمكنك إلغاء أو تعديل حجزك من خلال لوحة التحكم الخاصة بك. تعتمد سياسة الإلغاء على الوكالة وشروط الحجز الخاصة بك. يرجى مراجعة شروط الحجز للحصول على التفاصيل.'
    },
    {
      question: 'هل التأمين مشمول في سعر الإيجار؟',
      answer: 'معظم السيارات تشمل التأمين الأساسي في سعر الإيجار اليومي. ومع ذلك، قد ترغب في تغطية إضافية للحصول على حماية أفضل. يمكنك إضافة تغطية تأمينية إضافية أثناء عملية الحجز.'
    },
    {
      question: 'ماذا يحدث إذا تعرضت السيارة لحادث؟',
      answer: 'في حالة وقوع حادث، اتصل فورًا بوكالة التأجير وبالشرطة إذا لزم الأمر. سيغطي التأمين الأساسي معظم الأضرار، ولكن قد تكون مسؤولاً عن دفع نسبة خصم حسب شروط التأمين الخاصة بك.'
    },
    {
      question: 'هل يمكنني استئجار سيارة لمدة طويلة؟',
      answer: 'نعم، نقدم خيارات إيجار شهرية وطويلة الأمد بأسعار تنافسية. اتصل بوكالة التأجير مباشرة أو استخدم نموذج الاتصال الخاص بنا للحصول على عرض أسعار مخصص للإيجارات طويلة الأمد.'
    }
  ] : lang === 'en' ? [
    {
      question: 'How do I book a car on KiraCar?',
      answer: 'You can easily book a car on KiraCar by searching for your preferred vehicle, selecting your rental dates, and completing the booking process online. We offer a secure and fast booking process with instant confirmation.'
    },
    {
      question: 'What documents do I need to rent a car?',
      answer: 'You will need a valid driver\'s license, an ID card or passport, and a credit card for the deposit. Some agencies may require additional documents depending on the vehicle type.'
    },
    {
      question: 'Can I cancel or modify my booking?',
      answer: 'Yes, you can cancel or modify your booking through your dashboard. Cancellation policies depend on the agency and your specific booking terms. Please review your booking terms for details.'
    },
    {
      question: 'Is insurance included in the rental price?',
      answer: 'Most cars include basic insurance in the daily rental price. However, you may want additional coverage for better protection. You can add extra insurance coverage during the booking process.'
    },
    {
      question: 'What happens if the car has an accident?',
      answer: 'In case of an accident, immediately contact the rental agency and the police if necessary. Basic insurance will cover most damages, but you may be responsible for paying a deductible depending on your insurance terms.'
    },
    {
      question: 'Can I rent a car for a long period?',
      answer: 'Yes, we offer monthly and long-term rental options at competitive rates. Contact the rental agency directly or use our contact form to get a customized quote for long-term rentals.'
    }
  ] : [
    {
      question: 'Comment puis-je réserver une voiture sur KiraCar ?',
      answer: 'Vous pouvez facilement réserver une voiture sur KiraCar en recherchant votre véhicule préféré, en sélectionnant vos dates de location et en complétant le processus de réservation en ligne. Nous offrons un processus de réservation sécurisé et rapide avec confirmation instantanée.'
    },
    {
      question: 'Quels documents sont nécessaires pour louer une voiture ?',
      answer: 'Vous aurez besoin d\'un permis de conduire valide, d\'une carte d\'identité ou d\'un passeport, et d\'une carte bancaire pour la caution. Certaines agences peuvent demander des documents supplémentaires selon le type de véhicule.'
    },
    {
      question: 'Puis-je annuler ou modifier ma réservation ?',
      answer: 'Oui, vous pouvez annuler ou modifier votre réservation via votre tableau de bord. Les politiques d\'annulation dépendent de l\'agence et de vos conditions de réservation spécifiques. Veuillez consulter vos conditions de réservation pour plus de détails.'
    },
    {
      question: 'L\'assurance est-elle incluse dans le prix de location ?',
      answer: 'La plupart des voitures incluent une assurance de base dans le prix de location journalier. Cependant, vous pourriez vouloir une couverture supplémentaire pour une meilleure protection. Vous pouvez ajouter une assurance complémentaire lors du processus de réservation.'
    },
    {
      question: 'Que se passe-t-il en cas d\'accident avec la voiture ?',
      answer: 'En cas d\'accident, contactez immédiatement l\'agence de location et la police si nécessaire. L\'assurance de base couvrira la plupart des dommages, mais vous pourriez être responsable du paiement d\'une franchise selon vos conditions d\'assurance.'
    },
    {
      question: 'Puis-je louer une voiture pour une longue période ?',
      answer: 'Oui, nous proposons des options de location mensuelle et à long terme à des tarifs compétitifs. Contactez l\'agence de location directement ou utilisez notre formulaire de contact pour obtenir un devis personnalisé pour les locations longue durée.'
    }
  ]

  // Structured Data - Multiple schemas for better SEO
  const structuredData = [
    // Organization Schema
    generateOrganizationSchema(lang || 'fr'),

    // WebSite Schema with SearchAction
    generateWebSiteSchema(lang || 'fr'),

    // BreadcrumbList Schema
    generateBreadcrumbSchema([
      { name: 'Home', url: currentUrl }
    ]),

    // FAQ Schema
    generateFAQSchema(faqs)
  ]

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalUrl={currentUrl}
        openGraph={{
          'og:title': pageTitle,
          'og:description': pageDescription,
          'og:type': 'website',
          'og:url': currentUrl,
          'og:site_name': 'KiraCar',
          'og:locale': lang === 'ar' ? 'ar_MA' : lang === 'en' ? 'en_US' : 'fr_MA',
          'og:image': `${window.location.origin}/images/og-home.jpg`
        }}
        twitterCard={{
          'twitter:card': 'summary_large_image',
          'twitter:title': pageTitle,
          'twitter:description': pageDescription,
          'twitter:image': `${window.location.origin}/images/og-home.jpg`
        }}
        structuredData={structuredData}
        alternateUrls={[
          { hreflang: 'fr', href: getCanonicalUrl('fr') },
          { hreflang: 'en', href: getCanonicalUrl('en') },
          { hreflang: 'ar', href: getCanonicalUrl('ar') },
          { hreflang: 'x-default', href: getCanonicalUrl('fr') }
        ]}
      />
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-b from-gray-50 via-brand-50/80 to-gray-50 dark:from-gray-900 dark:via-brand-950/30 dark:to-background flex flex-col overflow-hidden pt-12 pb-4 sm:pt-16 sm:pb-8 md:pb-20"
        style={!isMobile ? { height: 'calc(100vh - 80px)' } : {}}
      >
        {/* Professional Ready-to-Use Background Animation */}
        <div className="absolute inset-0 overflow-hidden">
          {/* CSS-only Professional Animation */}
          <div className="absolute inset-0">
            {/* Animated Gradient Mesh */}
            <div 
              className="absolute inset-0 opacity-30"
              style={{
                background: `
                  radial-gradient(circle at 20% 50%, #facf0420 0%, transparent 50%),
                  radial-gradient(circle at 80% 20%, #facf0415 0%, transparent 50%),
                  radial-gradient(circle at 40% 80%, #facf0410 0%, transparent 50%),
                  linear-gradient(135deg, transparent 0%, #facf0405 50%, transparent 100%)
                `,
                animation: 'gradientShift 15s ease-in-out infinite'
              }}
            />
            
            {/* Floating Geometric Shapes */}
            <div 
              className="absolute top-20 left-20 w-32 h-32 opacity-10"
              style={{
                background: 'linear-gradient(45deg, #facf04, #eab308)',
                borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                animation: 'float 8s ease-in-out infinite, morph 12s ease-in-out infinite'
              }}
            />
            
            <div 
              className="absolute top-1/2 right-20 w-24 h-24 opacity-8"
              style={{
                background: 'linear-gradient(135deg, #facf0430, transparent)',
                borderRadius: '50%',
                animation: 'float 6s ease-in-out infinite reverse, pulse 4s ease-in-out infinite'
              }}
            />
            
            
            {/* Particle Trail Effect */}
            <div 
              className="absolute inset-0"
              style={{
                background: `
                  radial-gradient(2px 2px at 20px 30px, #facf0440, transparent),
                  radial-gradient(2px 2px at 40px 70px, #facf0430, transparent),
                  radial-gradient(1px 1px at 90px 40px, #facf0450, transparent),
                  radial-gradient(1px 1px at 130px 80px, #facf0440, transparent),
                  radial-gradient(2px 2px at 160px 30px, #facf0430, transparent)
                `,
                backgroundRepeat: 'repeat',
                backgroundSize: '200px 100px',
                animation: 'particleMove 25s linear infinite'
              }}
            />
          </div>
          
          {/* CSS Keyframes */}
          <style dangerouslySetInnerHTML={{
            __html: `
              @keyframes gradientShift {
                0%, 100% { 
                  filter: hue-rotate(0deg) brightness(1); 
                  transform: scale(1) rotate(0deg);
                }
                25% { 
                  filter: hue-rotate(10deg) brightness(1.1); 
                  transform: scale(1.05) rotate(1deg);
                }
                50% { 
                  filter: hue-rotate(-5deg) brightness(0.9); 
                  transform: scale(0.95) rotate(-1deg);
                }
                75% { 
                  filter: hue-rotate(15deg) brightness(1.05); 
                  transform: scale(1.02) rotate(0.5deg);
                }
              }
              
              @keyframes float {
                0%, 100% { transform: translateY(0px) translateX(0px); }
                25% { transform: translateY(-20px) translateX(10px); }
                50% { transform: translateY(-10px) translateX(-5px); }
                75% { transform: translateY(-15px) translateX(5px); }
              }
              
              @keyframes morph {
                0%, 100% { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; }
                25% { border-radius: 58% 42% 75% 25% / 76% 46% 54% 24%; }
                50% { border-radius: 50% 50% 33% 67% / 55% 27% 73% 45%; }
                75% { border-radius: 33% 67% 58% 42% / 63% 68% 32% 37%; }
              }
              
              @keyframes pulse {
                0%, 100% { transform: scale(1); opacity: 0.8; }
                50% { transform: scale(1.2); opacity: 0.4; }
              }
              
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
              
              @keyframes particleMove {
                0% { transform: translateX(0px) translateY(0px); }
                25% { transform: translateX(50px) translateY(-25px); }
                50% { transform: translateX(-30px) translateY(-50px); }
                75% { transform: translateX(20px) translateY(-25px); }
                100% { transform: translateX(0px) translateY(0px); }
              }
            `
          }} />
        </div>

        {/* Hero Content */}
        <div className="container mx-auto px-4 relative z-10 flex-1 flex flex-col items-center justify-center text-center md:flex-row md:items-start md:text-left hero-content-positioning pb-0 sm:pb-2 md:pb-6 lg:pb-8">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 md:gap-16 items-center w-full max-w-6xl mx-auto">
            {/* Left Content */}
            <motion.div
              {...fadeInLeft}
              className="space-y-1 sm:space-y-2 md:space-y-8 rtl:order-1 w-full flex flex-col items-center md:items-start lg:items-start"
              style={{
                transform: window.innerHeight < 700 ? 'translateY(-20px)' : 'translateY(0)',
                transition: 'transform 0.3s ease'
              }}
            >
              <div className="space-y-1 sm:space-y-2 md:space-y-6 w-full flex flex-col items-center md:items-start rtl:items-center md:rtl:items-start">
                <h1 
                  className={`hero-title-mobile text-4xl sm:text-5xl md:text-5xl lg:text-6xl xl:text-7xl ${currentLang === 'ar' && isMobile ? 'font-zain' : 'font-poppins'} font-black text-foreground leading-tight w-full text-center md:text-left rtl:text-center md:rtl:text-right`}
                >
                  {t('findBookRental')}{' '}
                  <span className="text-[#facf04] relative inline-block">
                    {t('easySteps').split(' ')[0]}
                    <img 
                      src="/images/herotextunderLine.png" 
                      alt="underline" 
                      className="absolute -bottom-1 left-0 w-full h-auto rtl:right-0 rtl:left-auto rtl:-bottom-3"
                    />
                  </span>{' '}
                  {t('easySteps').split(' ').slice(1).join(' ')}
                </h1>
                
                <p className="text-sm md:text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-lg mx-auto md:mx-0">
                  {t('getCarWherever')}
                </p>
              </div>
            </motion.div>

            {/* Right Content - Hero Car Image (Hidden on Mobile) */}
            <motion.div
              {...fadeInRight}
              transition={{ ...fadeInRight.transition, delay: shouldReduceMotion ? 0 : 0.2 }}
              className="relative rtl:order-2 hidden md:block"
            >
              <div className="relative">
                <img
                  src="/images/heroCar.png"
                  alt="Premium Car"
                  className="w-full h-auto rtl:scale-x-[-1] max-h-[150px] md:max-h-none object-contain"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Search Panel - Positioned Below Hero Text */}
        <div className="w-full md:absolute md:left-0 md:right-0 md:top-2/3 md:transform md:-translate-y-1/2 z-20 mt-8 md:mt-0">
          <motion.div
            {...fadeInUp}
            transition={{ ...fadeInUp.transition, delay: shouldReduceMotion ? 0 : 0.3 }}
            className="w-full max-w-[850px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16"
          >
            {isMobile ? (
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="default" className="w-full h-14 rounded-full text-lg font-bold shadow-lg">
                    <Search className="mr-3 h-6 w-6" />
                    {t('mobileSearch.trigger', 'Where to?')}
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="rounded-t-2xl max-h-[90vh] overflow-y-auto">
                  <SheetHeader className="mb-4">
                    <SheetTitle>{t('mobileSearch.title', 'Find your perfect car')}</SheetTitle>
                    <SheetDescription>
                      {t('mobileSearch.description', 'Select your location and dates to start.')}
                    </SheetDescription>
                  </SheetHeader>
                  <SearchPanel locations={mockLocations} isMobile={isMobile} />
                </SheetContent>
              </Sheet>
            ) : (
                <SearchPanel 
                  locations={mockLocations}
            />
            )}
          </motion.div>
              </div>
              
        {/* Car Brands Slider - Absolute Bottom of Hero */}
        <div className="md:absolute md:bottom-0 md:left-0 md:right-0 z-20 w-full mt-8 md:mt-0">
                  <BrandsSlider />
                </div>
      </section>

      {/* Featured Categories */}
      <section className="pt-16 md:pt-20 pb-6 md:pb-10 bg-gradient-to-b from-gray-50 to-white dark:from-background dark:to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true, margin: "-50px" }}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6"
          >
            <div className="text-left mb-4 sm:mb-0">
            <h2 className={`text-2xl md:text-3xl lg:text-4xl ${currentLang === 'ar' && isMobile ? 'font-zain' : 'font-poppins'} font-black text-foreground mb-2`}>
              {t('featuredCategories')}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl">
              {t('featuredCategoriesDesc')}
            </p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-sm font-medium flex-shrink-0"
              onClick={() => navigate(`/${lang || 'fr'}/cars`)}
            >
              Voir Tout
            </Button>
          </motion.div>

          {/* Categories Horizontal Scroll */}
          <div className="relative">
            {/* Left Fade Shadow - Stronger gradient to hide item edges */}
            {!isMobile && <div className="absolute left-0 top-0 bottom-0 w-16 md:w-20 bg-gradient-to-r from-white via-white/95 via-white/85 via-white/70 to-transparent dark:from-background dark:via-background/95 dark:via-background/85 dark:via-background/70 dark:to-transparent z-20 pointer-events-none" />}
            
            {/* Right Fade Shadow - Stronger gradient to hide item edges */}
            {!isMobile && <div className="absolute right-0 top-0 bottom-0 w-16 md:w-20 bg-gradient-to-l from-white via-white/95 via-white/85 via-white/70 to-transparent dark:from-background dark:via-background/95 dark:via-background/85 dark:via-background/70 dark:to-transparent z-20 pointer-events-none" />}
            
            {/* Navigation Arrows - Outside shadows and items */}
            {showScrollArrows && !isMobile ? (
              <>
                <button
                  onClick={scrollCategoriesLeft}
                  aria-label={t('common.scrollLeft', 'Scroll categories left')}
                  className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-30 bg-white dark:bg-gray-800 border border-gray-200 dark:border-border rounded-full p-3 shadow-xl hover:shadow-2xl transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-110"
                >
                  <ChevronLeft className="h-5 w-5 md:h-6 md:w-6 text-gray-700 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={scrollCategoriesRight}
                  aria-label={t('common.scrollRight', 'Scroll categories right')}
                  className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-30 bg-white dark:bg-gray-800 border border-gray-200 dark:border-border rounded-full p-3 shadow-xl hover:shadow-2xl transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-110"
                >
                  <ChevronRight className="h-5 w-5 md:h-6 md:w-6 text-gray-700 dark:text-gray-300" />
                </button>
              </>
            ) : null}

            <div 
              ref={setCategoriesScrollRef}
              className={`flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory scroll-smooth ${isMobile ? 'px-4' : 'px-20 md:px-24'}`}
            >
              {categoriesLoading ? (
                // Simple professional loading skeleton
                Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="flex-shrink-0 w-48 md:w-64 snap-center">
                    <div className="bg-white dark:bg-card border border-gray-100 dark:border-border rounded-xl h-full overflow-hidden">
                      {/* Category Info Skeleton */}
                      <div className="flex items-center justify-between mb-2 md:mb-3 p-3 md:p-3.5">
                        <div className="space-y-2">
                          <div className="h-4 bg-gray-200 dark:bg-muted rounded w-20"></div>
                          <div className="h-3 bg-gray-100 dark:bg-muted/70 rounded w-16"></div>
                        </div>
                        <div className="h-8 w-8 bg-gray-200 dark:bg-muted rounded-full"></div>
                      </div>
                      
                      {/* Category Image Skeleton */}
                      <div className="px-3 md:px-3.5 pb-3 md:pb-3.5">
                        <div className="h-14 md:h-20 bg-gray-200 dark:bg-muted rounded-lg"></div>
                      </div>
                    </div>
                  </div>
                ))
              
              ) : categoriesError ? (
                // Error state
                <div className="flex-shrink-0 w-full text-center py-8">
                  <p className="text-red-500 mb-4">Error loading categories: {categoriesError}</p>
                  <Button 
                    onClick={() => window.location.reload()} 
                    variant="outline"
                    size="sm"
                  >
                    Retry
                  </Button>
                </div>
              ) : categories.length > 0 ? (
                categories.filter(category => category.car_count > 0).map((category, index) => (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, x: shouldReduceMotion ? 0 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ 
                      duration: shouldReduceMotion ? 0 : 0.5, 
                      delay: shouldReduceMotion ? 0 : index * 0.05,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                    viewport={{ once: true }}
                    className="flex-shrink-0 w-48 md:w-64 snap-center"
                  >
                    <div 
                      className="category-item bg-white dark:bg-card border border-gray-100 dark:border-border rounded-lg hover:shadow-lg transition-all duration-300 group cursor-pointer h-full"
                      onClick={() => {
                        const categorySlug = mapCategoryNameToSlug(category.name)
                        const categoryUrl = generateCategoryUrl(categorySlug, lang as 'fr' | 'en' | 'ar')
                        navigate(`/${lang || 'fr'}${categoryUrl}`)
                      }}
                    >
                      {/* Category Info */}
                      <div className="category-info flex items-center justify-between mb-2 md:mb-3 p-3 md:p-3.5">
                        <div>
                          <h3 className="title mb-1">
                            <span className="text-sm md:text-base font-semibold text-foreground hover:text-brand-600 transition-colors duration-300">
                              {category.name_fr || category.name}
                            </span>
                          </h3>
                          <p className="text-xs md:text-sm text-muted-foreground font-medium mb-0">
                            {category.car_count || 0} Cars
                          </p>
                        </div>
                        <div className="link-icon text-muted-foreground hover:text-brand-600 transition-colors duration-300">
                          <ArrowRight className="h-4 w-4 md:h-5 md:w-5" />
                        </div>
                      </div>
                      
                      {/* Category Image */}
                      <div className="category-img">
                        <div className="h-14 md:h-20 rounded-lg overflow-hidden relative flex items-center justify-end">
                          {category.image_url ? (
                            <img
                              src={category.image_url}
                              alt={category.name_fr || category.name}
                              width="80"
                              height="80"
                              loading="lazy"
                              decoding="async"
                              className="h-full w-auto object-contain max-w-full"
                              onError={(e) => {
                                // Fallback to emoji if image fails to load
                                const target = e.target as HTMLImageElement
                                target.style.display = 'none'
                                const parent = target.parentElement
                                if (parent) {
                                  parent.innerHTML = `
                                    <div class="h-24 rounded-lg flex items-center justify-center relative overflow-hidden">
                                      <div class="text-lg opacity-70 relative z-10">
                                        ${category.icon_emoji || '🚗'}
                                      </div>
                                    </div>
                                  `
                                }
                              }}
                            />
                          ) : (
                            <div className="h-20 rounded-lg flex items-center justify-center relative overflow-hidden">
                              <div className="text-lg opacity-70 relative z-10">
                                {category.icon_emoji || '🚗'}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : categories.filter(category => category.car_count > 0).length === 0 ? (
                // Empty state when no categories with cars
                <div className="flex-shrink-0 w-full text-center py-8">
                  <div className="text-muted-foreground">
                    <CarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Aucune catégorie avec des voitures disponibles pour le moment.</p>
                  </div>
                </div>
              ) : null}
            </div>
            

          </div>
        </div>
      </section>

      {/* Popular Destinations Section */}
      <PopularDestinations />

      {/* FAQ Section */}
      <FAQ
        faqs={faqs}
        title={lang === 'ar' ? 'الأسئلة المتكررة' : lang === 'en' ? 'Frequently Asked Questions' : 'Questions Fréquemment Posées'}
        description={lang === 'ar' ? 'ابحث عن إجابات للأسئلة الأكثر شيوعًا' : lang === 'en' ? 'Find answers to the most common questions' : 'Trouvez les réponses aux questions les plus courantes'}
        className="bg-background"
      />

      {/* Call to Action - Join KiraCar Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-background via-muted/30 to-background relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.05]">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Ccircle cx='20' cy='20' r='1'/%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Header */}
          <div className="text-center mb-16">
          <motion.div
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 20 }}
            whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: shouldReduceMotion ? 0 : 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
          >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              {t('joinKiraCar') || 'Rejoindre KiraCar'}
            </h2>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                {t('chooseYourPath') || 'Choisissez votre parcours et commencez votre voyage avec nous'}
            </p>
          </motion.div>
          </div>

          {/* Cards Grid */}
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Client Card */}
            <motion.div
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: shouldReduceMotion ? 0 : 0.8, delay: shouldReduceMotion ? 0 : 0.1 }}
              viewport={{ once: true }}
              className="w-full"
            >
              <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-card to-card/80 group">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-brand-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-105 transition-transform duration-300">
                    <User className="w-8 h-8 text-brand-600" />
                </div>
                  <CardTitle className="text-2xl font-bold text-card-foreground">
                  {t('becomeClient') || 'Devenir Client'}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {t('clientDescription') || 'Louez des voitures facilement et rapidement'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4 mb-8">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-brand-600 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Accès à une large gamme de véhicules</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-brand-600 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Réservation instantanée</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-brand-600 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Support client 24/7</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-brand-600 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Prix transparents</span>
                    </div>
                  </div>
                <Button
                  onClick={() => navigate(`/${currentLang}/signup?type=client`)}
                    className="w-full bg-brand-500 hover:bg-brand-600 text-black font-semibold h-12"
                    size="lg"
                >
                  {t('signupAsClient') || "S'inscrire comme Client"}
                    <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Agency Card */}
            <motion.div
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: shouldReduceMotion ? 0 : 0.8, delay: shouldReduceMotion ? 0 : 0.2 }}
              viewport={{ once: true }}
              className="w-full"
            >
              <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-card to-card/80 group">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-105 transition-transform duration-300">
                    <Building2 className="w-8 h-8 text-blue-600" />
                </div>
                  <CardTitle className="text-2xl font-bold text-card-foreground">
                  {t('becomeAgency') || 'Devenir Agence'}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {t('agencyDescription') || 'Gérez votre flotte et développez votre business'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4 mb-8">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Gestion complète de flotte</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Tableau de bord avancé</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Paiements automatisés</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Analytics détaillés</span>
                    </div>
                  </div>
                <Button
                  onClick={() => navigate(`/${currentLang}/signup?type=agency`)}
                    variant="outline"
                    className="w-full border-blue-200 hover:bg-blue-50 hover:border-blue-300 dark:border-blue-800 dark:hover:bg-blue-950 font-semibold h-12"
                    size="lg"
                >
                  {t('signupAsAgency') || "S'inscrire comme Agence"}
                    <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  )
}

export default Home 